import React, { useState, useEffect } from "react";
import {
  Box, Typography, CircularProgress, Button,
  Checkbox, Chip, IconButton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ExtensionIcon from "@mui/icons-material/Extension";
import MapIcon from "@mui/icons-material/AccountTree";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

export default function MainSubMapping() {
  const [mainModules, setMainModules] = useState([]);
  const [subModules,  setSubModules]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);

  // panel state: { mode: 'map'|'view', mainId }
  const [panel, setPanel] = useState(null);
  // local draft of checked sub-module ids for the open Map panel
  const [checked, setChecked] = useState(new Set());
  // view panel data
  const [viewData,    setViewData]    = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchAll = async (autoOpenId = null) => {
    setLoading(true);
    try {
      const [mainRes, subRes] = await Promise.all([
        axiosConfig.get("home/home-page-module/"),
        axiosConfig.get("home/home-page-sub-module/"),
      ]);
      const mains = mainRes.data?.response || [];
      setMainModules(mains);
      setSubModules(subRes.data?.response || []);

      const firstId = autoOpenId ?? mains[0]?.id;
      if (firstId) {
        setPanel({ mode: "map", mainId: firstId });
        const res = await axiosConfig.get("home/module-tag-with-sub-module/", { params: { module: firstId } });
        const mapped = res.data?.response?.[0]?.sub_modules || [];
        setChecked(new Set(mapped.map((s) => s.id)));
      }
    } catch {
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openMap = async (mainId) => {
    setChecked(new Set());
    setPanel({ mode: "map", mainId });
    try {
      const res = await axiosConfig.get("home/module-tag-with-sub-module/", { params: { module: mainId } });
      const mapped = res.data?.response?.[0]?.sub_modules || [];
      setChecked(new Set(mapped.map((s) => s.id)));
    } catch {
      toast.error("Failed to load existing mapping");
    }
  };

  const openView = async (mainId) => {
    setPanel({ mode: "view", mainId });
    setViewData([]);
    setViewLoading(true);
    try {
      const res = await axiosConfig.get("home/module-tag-with-sub-module/", { params: { module: mainId } });
      setViewData(res.data?.response?.[0]?.sub_modules || []);
    } catch {
      toast.error("Failed to load mapped sub-modules");
    } finally {
      setViewLoading(false);
    }
  };

  const closePanel = () => setPanel(null);

  const toggleCheck = (subId) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(subId) ? next.delete(subId) : next.add(subId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!panel) return;
    const { mainId } = panel;

    setSaving(true);
    try {
      await axiosConfig({
        method: "post",
        url: "home/module-tag-with-sub-module/",
        data: { module: mainId, sub_module: [...checked] },
      });
      toast.success("Mapping saved");
      await fetchAll(panel.mainId);
    } catch {
      toast.error("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  const activeMain = panel ? mainModules.find((m) => m.id === panel.mainId) : null;
  const mappedSubs = panel
    ? subModules.filter((s) => s.home_page_module === panel.mainId)
    : [];

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", boxShadow: "0 4px 20px rgba(4,52,58,0.3)", p: "20px 28px", mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <SwapHorizIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>Main → Sub Module Mapping</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>Select a main module to map or view its sub-modules</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
          <CircularProgress sx={{ color: "#016551" }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

          {/* Main module list */}
          <Box sx={{ flex: panel ? "0 0 340px" : 1, background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ background: "#04343a", p: "12px 20px", display: "flex", alignItems: "center", gap: 1 }}>
              <ViewModuleIcon sx={{ color: "#fff", fontSize: 16 }} />
              <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>Main Modules</Typography>
            </Box>

            {mainModules.length === 0 ? (
              <Typography sx={{ p: 3, color: "#94a3b8", fontSize: "13px" }}>No main modules found</Typography>
            ) : (
              mainModules.map((m) => {
                const count     = subModules.filter((s) => s.home_page_module === m.id).length;
                const isActive  = panel?.mainId === m.id;
                return (
                  <Box
                    key={m.id}
                    sx={{
                      p: "14px 20px",
                      borderBottom: "1px solid #f1f5f9",
                      borderLeft: isActive ? "3px solid #016551" : "3px solid transparent",
                      background: isActive ? "rgba(1,101,81,0.05)" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.name}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.3 }}>
                        {count} sub-module{count !== 1 ? "s" : ""} mapped
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <Button
                        size="small"
                        startIcon={<MapIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => openMap(m.id)}
                        sx={{
                          background: isActive && panel?.mode === "map" ? "#016551" : "transparent",
                          color: isActive && panel?.mode === "map" ? "#fff" : "#016551",
                          border: "1px solid #016551",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          textTransform: "none",
                          "&:hover": { background: "#016551", color: "#fff" },
                        }}
                      >
                        Map
                      </Button>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => openView(m.id)}
                        sx={{
                          background: isActive && panel?.mode === "view" ? "#04343a" : "transparent",
                          color: isActive && panel?.mode === "view" ? "#fff" : "#04343a",
                          border: "1px solid #04343a",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          textTransform: "none",
                          "&:hover": { background: "#04343a", color: "#fff" },
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Right panel — Map or View */}
          {panel && activeMain && (
            <Box sx={{ flex: 1, background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              {/* Panel header */}
              <Box sx={{ background: panel.mode === "map" ? "#016551" : "#04343a", p: "12px 20px", display: "flex", alignItems: "center", gap: 1 }}>
                <ExtensionIcon sx={{ color: "#fff", fontSize: 16 }} />
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "14px", flex: 1 }}>
                  {panel.mode === "map" ? `Map sub-modules to "${activeMain.name}"` : `Sub-modules of "${activeMain.name}"`}
                </Typography>
                <IconButton size="small" onClick={closePanel} sx={{ color: "#fff" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ p: 2.5 }}>
                {panel.mode === "view" ? (
                  // ── View mode ──────────────────────────────────────────────
                  viewLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={24} sx={{ color: "#016551" }} />
                    </Box>
                  ) : viewData.length === 0 ? (
                    <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>No sub-modules mapped to this main module yet.</Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {viewData.map((s, i) => (
                        <Chip
                          key={s.id ?? i}
                          icon={<ExtensionIcon sx={{ fontSize: "14px !important", color: "#016551 !important" }} />}
                          label={s.name}
                          sx={{ background: "rgba(1,101,81,0.08)", color: "#016551", fontWeight: 600, fontSize: "12px", border: "1px solid #bbf7d0" }}
                        />
                      ))}
                    </Box>
                  )
                ) : (
                  // ── Map mode ───────────────────────────────────────────────
                  <>
                    <Typography sx={{ fontSize: "12px", color: "#64748b", mb: 2 }}>
                      Check sub-modules to assign them to <strong>{activeMain.name}</strong>. Uncheck to remove.
                    </Typography>

                    {subModules.length === 0 ? (
                      <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>No sub-modules available.</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {subModules.map((s) => {
                          const isChecked = checked.has(s.id);
                          return (
                            <Box
                              key={s.id}
                              onClick={() => toggleCheck(s.id)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: "10px 14px",
                                borderRadius: "8px",
                                border: isChecked ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                                background: isChecked ? "rgba(1,101,81,0.05)" : "#fafafa",
                                cursor: "pointer",
                                "&:hover": { background: isChecked ? "rgba(1,101,81,0.09)" : "#f1f5f9" },
                              }}
                            >
                              <Checkbox
                                checked={isChecked}
                                onChange={() => toggleCheck(s.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0, color: "#cbd5e1", "&.Mui-checked": { color: "#016551" } }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: isChecked ? "#016551" : "#1e293b" }}>
                                  {s.name}
                                </Typography>
                                {s.home_page_module && s.home_page_module !== panel.mainId && (
                                  <Typography sx={{ fontSize: "11px", color: "#f59e0b" }}>
                                    Currently mapped to another module
                                  </Typography>
                                )}
                              </Box>
                              {isChecked && (
                                <Chip label="Selected" size="small" sx={{ height: 18, fontSize: "10px", background: "#016551", color: "#fff" }} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon />}
                        sx={{ background: "linear-gradient(135deg, #016551, #014d3d)", color: "#fff", fontWeight: 600, borderRadius: "8px", px: 3, boxShadow: "0 4px 12px rgba(1,101,81,0.3)", textTransform: "none", "&:hover": { background: "linear-gradient(135deg, #1db954, #17a349)" }, "&:disabled": { background: "#94a3b8" } }}
                      >
                        {saving ? "Saving…" : "Save Mapping"}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
